import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Plus, Search } from "lucide-react";
import { Icon, Rows, Spinner } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useMe } from "../../app/hooks/useSession.js";
import { useBandTeam, useBandSeatInvites } from "../../app/hooks/useBandTeam.js";
import { useFriends, bandInitials } from "../../app/hooks/useFriends.js";
import { BandAvatar } from "../shared/BandAvatar.js";
import { CrewSeatRow, PendingInviteRow, InviteCrewModal, CrewError, invitedName } from "./CrewSeatRow.js";
import { AddFriendModal } from "./AddFriendModal.js";
import { PartnerBox } from "./PartnerBox.js";
import styles from "./MobileContactsScreen.module.scss";

/**
 * The MOBILE `/contacts` screen (ART-193) — the phone counterpart of the desktop four-card grid
 * (`ContactsScreen`). Ryan's mockup is a single scroll: FRIENDS as a horizontal rail of circle avatars, then
 * the crew list with an inline search + add. Top 8, the Friends *card*, Teams, the A/B/C letter separators,
 * the All/Band toggle AND the band header ("Whitsett") box are all dropped on mobile — the crew list is one
 * flat run of cards (Ryan, ART-193 feedback 2026-09-16: "I dont want everyone separated by letter… kill the
 * whitsett box"). The PARTNER box above the list is net-new member-side feature work (partner-link
 * invite/list/remove) and lands in its own pass (ART-193b).
 *
 * Render-context free (mount-testable via `renderWithBand`): navigation rides the callbacks the route wrapper
 * supplies, the same contract as `MobileContactProfileScreen`. The shell header carries the "Contacts" title,
 * so the visible h1 is sr-only.
 */
export function MobileContactsScreen({
  onOpenBand,
  onOpenPerson,
}: {
  onOpenBand?: (bandId: string) => void;
  onOpenPerson?: (personId: string) => void;
}) {
  return (
    <div className={styles.contacts} data-testid="mobile-contacts">
      <h1 className={styles.srOnly}>Contacts</h1>
      <FriendsStrip onOpenBand={onOpenBand} />
      <PartnerBox />
      <CrewList onOpenPerson={onOpenPerson} />
    </div>
  );
}

/**
 * FRIENDS — a horizontal rail of circle avatars (Ryan: "circle icons that you can scroll left to right") with
 * a black + Add. The whole rail is `friends:manage`-gated: a seat that can't read friendships never mounts it
 * (no read, no 403), so the outer gate returns null before the `useFriends` read — the `FriendsCard` pattern.
 * Friend avatars are CIRCLES here by explicit request, overriding the app-wide band=rounded-square shape.
 */
function FriendsStrip({ onOpenBand }: { onOpenBand?: (bandId: string) => void }) {
  const canManage = useCapability("friends:manage");
  if (!canManage) return null;
  return <FriendsStripInner onOpenBand={onOpenBand} />;
}

function FriendsStripInner({ onOpenBand }: { onOpenBand?: (bandId: string) => void }) {
  // `friends:send` is the tighter AV-only token — only an Artist-View seat may originate a request, so the
  // black + Add shows only for a caller the gate will accept (the `FriendsCard` split).
  const canSend = useCapability("friends:send");
  const list = useFriends();
  const [adding, setAdding] = useState(false);

  const active = list.data?.active ?? [];

  return (
    <section className={styles.friends} aria-label="Friends">
      <header className={styles.friendsHead}>
        <p className={styles.eyebrow}>
          Friends {active.length > 0 && <span className={styles.count}>{active.length}</span>}
        </p>
        {canSend && (
          <button type="button" className={styles.addBtn} onClick={() => setAdding(true)}>
            <Icon icon={Plus} size={16} /> Add
          </button>
        )}
      </header>

      {list.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={20} label="Loading friends…" />
        </div>
      ) : active.length === 0 ? (
        <p className={styles.friendsEmpty}>
          {canSend ? "No friends yet — search for an artist to send a request." : "No friends yet."}
        </p>
      ) : (
        <ul className={styles.rail}>
          {active.map((f) => (
            <li key={f.friendshipId}>
              <button
                type="button"
                className={styles.friendCell}
                onClick={() => f.friendBandId && onOpenBand?.(f.friendBandId)}
              >
                <BandAvatar
                  bandId={f.friendBandId}
                  avatarMediaId={f.avatarMediaId}
                  initials={bandInitials(f.bandName)}
                  size="xl"
                  shape="circle"
                />
                <span className={styles.friendName}>{f.bandName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding && <AddFriendModal onClose={() => setAdding(false)} />}
    </section>
  );
}

/**
 * The crew list — the active seats and any pending invites in ONE flat run of cards, filtered by one search
 * field that carries the inline + Add crew (Ryan: "+Add on the same line as the search box"). No band header
 * box, no letter separators, no All/Band toggle, no Pro party grouping (Ryan, ART-193 feedback: "kill the
 * whitsett box… I dont want everyone separated by letter").
 */
function CrewList({ onOpenPerson }: { onOpenPerson?: (personId: string) => void }) {
  const canManage = useCapability("band:manage");
  const team = useBandTeam();
  const invites = useBandSeatInvites(canManage);
  const me = useMe();

  const [search, setSearch] = useState("");
  const [inviting, setInviting] = useState(false);

  // The search PINS to the top of the scroll area (below the shell header) once it reaches it. It uses a
  // viewport-`position: fixed` state rather than `position: sticky`, because sticky lives INSIDE the shell's
  // bouncing content layer (ART-173's `overscroll-behavior: contain`) and rides the elastic overscroll with
  // it; a fixed element is positioned against the viewport and so is immune, exactly like the shell header
  // (which never bounces because it is a sibling OUTSIDE that layer). A 0-height sentinel at the search's
  // resting spot, observed against the scroll container, flips the pinned state; the dock reserves the
  // search's height so the list never jumps when it leaves flow.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(false);

  const myPersonId = me.data?.person?.personId;
  const seats = useMemo(
    () => (team.data?.items ?? []).filter((s) => s.seatId && !s.isOwner),
    [team.data],
  );
  const pending = useMemo(() => invites.data?.items ?? [], [invites.data]);

  const q = search.trim().toLowerCase();
  const shownSeats = seats.filter(
    (s) => !q || [s.displayName, s.email].filter(Boolean).join(" ").toLowerCase().includes(q),
  );
  const shownPending = pending.filter(
    (i) => !q || [i.firstName, i.lastName, i.email].filter(Boolean).join(" ").toLowerCase().includes(q),
  );

  const totalPeople = seats.length + pending.length;
  const shownCount = shownSeats.length + shownPending.length;
  const showToolbar = totalPeople > 0 || canManage;

  // Attach once the toolbar (and its sentinel) actually render — the sentinel only exists when `showToolbar`,
  // which flips true after the team read lands. Outside the mobile shell (tests) there is no `<main>` scroller,
  // so the observer never attaches and the search stays in flow — the correct no-op.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scroller = sentinel?.closest("main");
    if (!sentinel || !scroller) return;
    const io = new IntersectionObserver(([entry]) => setPinned(!entry.isIntersecting), {
      root: scroller,
      threshold: 0,
    });
    io.observe(sentinel);
    return () => io.disconnect();
  }, [showToolbar]);

  // Seats + pending invites, merged into ONE flat run of cards, sorted by the same name the row shows — no
  // letter dividers (Ryan, ART-193 feedback: "I dont want everyone separated by letter like this").
  const rows = useMemo(() => {
    type Entry = { sortName: string; node: ReactNode };
    const entries: Entry[] = [
      ...shownSeats.map((s) => ({
        sortName: (s.displayName || s.email || "").trim(),
        node: (
          <CrewSeatRow
            key={s.seatId}
            seat={s}
            isSelf={!!myPersonId && s.personId === myPersonId}
            onOpen={() => (s.personId ? onOpenPerson?.(s.personId) : undefined)}
          />
        ),
      })),
      ...shownPending.map((i) => ({
        sortName: invitedName(i),
        node: <PendingInviteRow key={i.inviteId} invite={i} />,
      })),
    ];
    entries.sort((a, b) => a.sortName.localeCompare(b.sortName, undefined, { sensitivity: "base" }));
    return entries.map((e) => e.node);
  }, [shownSeats, shownPending, myPersonId, onOpenPerson]);

  return (
    <section className={styles.crew} aria-label="Contacts">
      {/* Search + inline Add crew. The search shows once there is somebody to search for; the add door is
          band:manage-only. The dock reserves the toolbar's height so the list does not jump when the toolbar
          pins (leaves flow). */}
      {showToolbar && (
        <>
          <div ref={sentinelRef} className={styles.sentinel} aria-hidden />
          <div className={styles.dock} data-pinned={pinned || undefined}>
            <div className={styles.toolbar}>
              <div className={styles.searchWrap}>
                <Icon icon={Search} size={16} className={styles.searchIcon} />
                <input
                  type="search"
                  className={styles.search}
                  placeholder="Search name, role, email"
                  aria-label="Search contacts"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {canManage && (
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => setInviting(true)}
                  aria-label="Add crew"
                >
                  <Icon icon={Plus} size={16} /> Add
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <div className={styles.list}>
        {team.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={20} label="Loading crew…" />
          </div>
        ) : team.isError ? (
          <CrewError error={team.error} onRetry={() => void team.refetch()} />
        ) : totalPeople === 0 ? (
          <p className={styles.listEmpty}>No crew yet.</p>
        ) : shownCount === 0 ? (
          <p className={styles.listEmpty}>No matches.</p>
        ) : (
          <div className={styles.contactCards}>
            <Rows>{rows}</Rows>
          </div>
        )}
      </div>

      {inviting && <InviteCrewModal onClose={() => setInviting(false)} />}
    </section>
  );
}
